/**
 * Utility functions for handling default images based on content type
 * All default images are served locally from /public/images/ for fast rendering
 */

// Default image mappings for different content types
// All images are stored locally in /public/images/ directory for optimal performance
const DEFAULT_IMAGES = {
  blog: {
    small: "/images/blog-post-400x225.png",
    medium: "/images/blog-post-800x450.png",
    large: "/images/blog-post-1200x675.png",
  },
  event: {
    small: "/images/events-400x225.png",
    medium: "/images/events-800x450.png",
    large: "/images/events-1200x675.png",
  },
  gallery: {
    small: "/images/image-gallery-400x225.png",
    medium: "/images/image-gallery-800x450.png",
    large: "/images/image-gallery-1200x675.png",
  },
  "media-coverage": {
    small: "/images/media-coverage-400x225.png",
    medium: "/images/media-coverage-800x450.png",
    large: "/images/media-coverage-1200x675.png",
  },
  "press-release": {
    small: "/images/press-release-400x225.png",
    medium: "/images/press-release-800x450.png",
    large: "/images/press-release-1200x675.png",
  },
  video: {
    small: "/images/video-gallery-400x225.png",
    medium: "/images/video-gallery-800x450.png",
    large: "/images/video-gallery-1200x675.png",
  },
  page: {
    small: "/images/blog-post-400x225.png", // Use blog post as default for pages
    medium: "/images/blog-post-800x450.png",
    large: "/images/blog-post-1200x675.png",
  },
  slider: {
    small: "/images/blog-post-400x225.png", // Use blog post as default for sliders
    medium: "/images/blog-post-800x450.png",
    large: "/images/blog-post-1200x675.png",
  },
  "members-list": {
    small: "/images/blog-post-400x225.png", // Use blog post as default for members list
    medium: "/images/blog-post-800x450.png",
    large: "/images/blog-post-1200x675.png",
  },
};

/**
 * Get the appropriate default image for a content type and size
 * @param {string} contentType - The type of content (blog, event, gallery, etc.)
 * @param {string} size - The size of the image (small, medium, large)
 * @returns {string} The path to the default image
 */
export function getDefaultImage(contentType, size = "medium") {
  const typeImages = DEFAULT_IMAGES[contentType];
  if (!typeImages) {
    // Fallback to blog post images if content type not found
    return DEFAULT_IMAGES["blog"][size] || DEFAULT_IMAGES["blog"]["medium"];
  }

  return typeImages[size] || typeImages["medium"];
}

/**
 * Get the featured image for a post, falling back to default if not provided
 * @param {Object} post - The post object
 * @param {string} size - The size of the image (small, medium, large)
 * @returns {string} The path to the featured image or default image
 */
export function getFeaturedImage(post, size = "medium") {
  if (!post) return getDefaultImage("blog", size);

  // Check if post has a featured image
  if (post.featuredImage && post.featuredImage.trim() !== "") {
    return post.featuredImage;
  }

  // Check if post has an alternate featured image
  if (
    post.alternateFeaturedImage &&
    post.alternateFeaturedImage.trim() !== ""
  ) {
    return post.alternateFeaturedImage;
  }

  // Check if post has a mobile slider image
  if (post.mobileSliderImage && post.mobileSliderImage.trim() !== "") {
    return post.mobileSliderImage;
  }

  // Check if post has an imageUrl (for sliders)
  if (post.imageUrl && post.imageUrl.trim() !== "") {
    return post.imageUrl;
  }

  // Fall back to default image based on content type
  const contentType = post.type || "blog";
  return getDefaultImage(contentType, size);
}

/**
 * Get responsive image sources for a post
 * @param {Object} post - The post object
 * @returns {Object} Object with srcSet and sizes for responsive images
 */
export function getResponsiveImageSources(post) {
  const contentType = post?.type || "blog";

  const smallImage = getDefaultImage(contentType, "small");
  const mediumImage = getDefaultImage(contentType, "medium");
  const largeImage = getDefaultImage(contentType, "large");

  return {
    src: getFeaturedImage(post, "medium"),
    srcSet: `${smallImage} 400w, ${mediumImage} 800w, ${largeImage} 1200w`,
    sizes: "(max-width: 400px) 400px, (max-width: 800px) 800px, 1200px",
  };
}

/**
 * Check if an image URL is a default image
 * @param {string} imageUrl - The image URL to check
 * @returns {boolean} True if the image is a default image
 */
export function isDefaultImage(imageUrl) {
  if (!imageUrl) return false;

  const defaultImagePaths = Object.values(DEFAULT_IMAGES).flatMap((images) =>
    Object.values(images)
  );
  return defaultImagePaths.some((defaultPath) =>
    imageUrl.includes(defaultPath)
  );
}

/**
 * Get all available content types that have default images
 * @returns {Array} Array of content type strings
 */
export function getAvailableContentTypes() {
  return Object.keys(DEFAULT_IMAGES);
}

/**
 * Get all available image sizes for a content type
 * @param {string} contentType - The content type
 * @returns {Array} Array of available sizes
 */
export function getAvailableSizes(contentType) {
  const typeImages = DEFAULT_IMAGES[contentType];
  return typeImages ? Object.keys(typeImages) : [];
}

/**
 * Check if an image URL is a local default image (not from database)
 * @param {string} imageUrl - The image URL to check
 * @returns {boolean} True if the image is a local default image
 */
export function isLocalDefaultImage(imageUrl) {
  if (!imageUrl) return false;

  // Check if it's a local default image path
  return imageUrl.startsWith("/images/") && isDefaultImage(imageUrl);
}

/**
 * Get optimized image props for Next.js Image component
 * @param {Object} post - The post object
 * @param {string} size - The size of the image (small, medium, large)
 * @returns {Object} Optimized props for Next.js Image component
 */
export function getOptimizedImageProps(post, size = "medium") {
  const imageSrc = getFeaturedImage(post, size);
  const isLocal = isLocalDefaultImage(imageSrc);

  return {
    src: imageSrc,
    alt: post?.title || "Post image",
    priority: isLocal, // Prioritize local images for faster loading
    quality: isLocal ? 90 : 75, // Higher quality for local images
    placeholder: isLocal ? "blur" : undefined,
    blurDataURL: isLocal
      ? "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
      : undefined,
  };
}

/**
 * Preload critical default images for better performance
 * @param {Array} contentTypes - Array of content types to preload
 * @returns {Array} Array of image URLs to preload
 */
export function getPreloadImages(contentTypes = ["blog", "event", "gallery"]) {
  const preloadImages = [];

  contentTypes.forEach((type) => {
    if (DEFAULT_IMAGES[type]) {
      // Preload medium size as it's most commonly used
      preloadImages.push(DEFAULT_IMAGES[type].medium);
    }
  });

  return preloadImages;
}

/**
 * Get all default image URLs for a content type
 * @param {string} contentType - The content type
 * @returns {Array} Array of all default image URLs for the content type
 */
export function getAllDefaultImagesForType(contentType) {
  const typeImages = DEFAULT_IMAGES[contentType];
  return typeImages ? Object.values(typeImages) : [];
}
